import { Badge, Box } from "@chakra-ui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { IFilterUser, TableUserProps } from "./interface";
import {
  PaginatorInfo,
  User,
  useListUsersQuery,
} from "../../generated/graphql";
import { createColumnHelper } from "@tanstack/react-table";
import Filter from "./Filter";
import update from "immutability-helper";
import DefaultTable, {
  TableImperativeHandle,
  ExtendedDefaultTableProps,
} from "../DefaultTable";
import NumberFormat from "../NumberFormat";
import TextCopy from "../TextCopy";
import moment from "moment";
import {
  getQuotationStatusColorScheme,
  getQuotationStatusName,
} from "../../helpers/global";

const columnHelper = createColumnHelper<User>();

const TableUser = forwardRef<
  TableImperativeHandle<User>,
  ExtendedDefaultTableProps<User, IFilterUser> & TableUserProps
>(
  (
    { filterParams, onFilterSubmit, page, onPageChange, onDelete, ...props },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      updateRow(data) {
        setDatas((datas) => {
          const index = datas.findIndex((elm) => elm.id === data.id);
          return update(datas, {
            [index]: {
              $set: { ...datas[index], ...data },
            },
          });
        });
      },
      refresh() {
        refetch();
      },
    }));

    const [localFilterParams, setLocalFilterParams] = useState<IFilterUser>();
    const [localPage, setLocalPage] = useState<number>(1);
    const [datas, setDatas] = useState<User[]>([]);

    const columns = useMemo(() => {
      return [
        columnHelper.accessor("name", {
          header: () => "Name",
          cell: (info) => (
            <TextCopy textCopy={info.getValue()}>
              <Box as="span">{info.getValue()}</Box>
            </TextCopy>
          ),
        }),
        columnHelper.accessor("email", {
          header: () => "Email",
          cell: (info) => info.getValue(),
        }),
      ];
    }, []);

    useEffect(() => {
      if (filterParams !== undefined) setLocalFilterParams(filterParams);
    }, [filterParams]);

    useEffect(() => {
      if (page !== undefined) setLocalPage(page);
    }, [page]);

    useEffect(() => {
      setLocalFilterParams({ ...localFilterParams });
    }, []);

    const [paginator, setPaginator] = useState<PaginatorInfo>();

    const { data, loading, refetch } = useListUsersQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        key: localFilterParams?.key || undefined,
      },
    });

    const [isLazyLoading, setIsLazyLoading] = useState(true);

    useEffect(() => {
      setPaginator(data?.listUsers.paginatorInfo);
      setDatas((data?.listUsers.data || []) as User[]);
    }, [data]);

    useEffect(() => {
      setIsLazyLoading(loading);
    }, [loading, setIsLazyLoading]);

    useEffect(() => {
      refetch({
        page: localPage,
        key: localFilterParams?.key || undefined,
      });
    }, [localFilterParams, localPage, refetch]);

    return (
      <DefaultTable
        data={datas}
        columns={columns}
        isLoading={loading || isLazyLoading}
        getRowId={(row) => row.id}
        filterComponent={
          <Filter
            value={localFilterParams}
            onSubmit={(data) => {
              setLocalPage(1);
              setLocalFilterParams(data);
              onFilterSubmit && onFilterSubmit(data);
            }}
          />
        }
        paginator={paginator}
        onPageChange={(page) => {
          setLocalPage(page);
          onPageChange && onPageChange(page);
        }}
        {...props}
      />
    );
  }
);

export default TableUser;
